import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fromJS, List, Map } from 'immutable';
import { extend, isEmpty, isUndefined, omit } from 'lodash';
import { Subject } from 'rxjs';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { IValueSwitch } from '../../../form-master/components/value-switch-field/value-switch-field.component';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { LoggerService } from '../../../shared/services/logger/logger.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { EditTaxBuilder } from './edit-tax-dialog.builder';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { TAX_RATE_LIMIT } from '../../../settings-data/settings-data.data';

export const DIALOG_TYPE = 'edit-tax-dialog';

export interface IEditTaxDialogData {
  tax: Tax;
  allTaxes: List<Tax>;
  locale: string;
}

@Component({
  selector: 'app-edit-tax-dialog',
  templateUrl: './edit-tax-dialog.component.html',
  styleUrls: ['./edit-tax-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [EditTaxBuilder],
})
export class EditTaxDialogComponent implements OnInit, OnDestroy {
  @Output() onUpdateTax: EventEmitter<Tax> = new EventEmitter<Tax>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  form: FormGroup;
  formKeys: any;
  dialogType: string;
  allTaxes: List<Tax>;
  currentTax: Tax;
  initialRatesSize: number;
  taxTypes: List<IValueSwitch>;
  locale: string;
  dialogHeader: string;
  deleteIcon: IIcon;
  trackingDialogName: TrackingDialogNamesEnum;
  trackingScreenName: TrackingScreenNamesEnum;
  trackingObject: ITrackingObject;
  saveAction: ITrackingAction;
  dismissAction: ITrackingAction;
  editTaxAdditionalProperties: any;

  constructor(
    private _dialogService: DialogService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _editTaxBuilder: EditTaxBuilder,
    private _translateService: TranslateService,
    private _loggerService: LoggerService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingObjectBuilder: TrackingObjectBuilder
  ) {}

  ngOnInit() {
    this.formKeys = this._editTaxBuilder.FORM_KEYS;
    this.dialogType = DIALOG_TYPE;
    this.deleteIcon = {
      name: 'close',
      size: IconSizesEnum.small,
    };
    this.trackingDialogName = TrackingDialogNamesEnum.edit_tax;
    this.trackingScreenName = TrackingScreenNamesEnum.edit_tax;
    this.trackingObject = this._trackingObjectBuilder.buildDialog(
      this.trackingDialogName
    );
    this.saveAction = this._trackingActionBuilder.buildButtonTapped(
      'save',
      this.trackingScreenName
    );
    this.dismissAction = this._trackingActionBuilder.buildButtonTapped(
      'cancel',
      this.trackingScreenName
    );

    this._dialogService.dialogTriggers$
      .pipe(
        filter((dialogTrigger: IDialogTrigger) => {
          return (
            dialogTrigger.type === this.dialogType &&
            dialogTrigger.action === DialogActionsEnum.open
          );
        }),
        takeUntil(this._destroyed)
      )
      .subscribe((dialogTrigger: IDialogTrigger) => {
        const dialogData: IEditTaxDialogData = dialogTrigger.data;
        const currentTax: Tax = dialogData.tax;
        const allTaxes: List<Tax> = dialogData.allTaxes;

        this.currentTax = currentTax;
        this.initialRatesSize = currentTax.rates.size;
        this.allTaxes = allTaxes;
        this.locale = dialogData.locale;
        this.dialogHeader = this._translateService.instant(
          'DIALOG.EDIT_TAX.TITLE',
          {
            taxName: currentTax.name,
          }
        );

        this.form = this._editTaxBuilder.buildForm(allTaxes, currentTax);
        this.taxTypes = this._editTaxBuilder.buildTaxTypes(
          this.trackingObject,
          this.trackingScreenName
        );

        this.editTaxAdditionalProperties = this._editTaxBuilder.buildEditTaxAdditionalProperties(
          this.currentTax,
          fromJS(this.form.value)
        );

        this.ratesFormArray.valueChanges
          .pipe(takeUntil(this._destroyed))
          .subscribe(() =>
            setTimeout(() => this._validateDuplicatedRates(this.ratesFormArray))
          );

        this.form.statusChanges
          .pipe(distinctUntilChanged(), takeUntil(this._destroyed))
          .subscribe(() => this._detectChanges());

        setTimeout(() => {
          this._validateDuplicatedRates(this.ratesFormArray);

          this._detectChanges();
        });
      });
  }

  get ratesFormArray(): FormArray {
    return this.form.get(this.formKeys.RATES) as FormArray;
  }

  addRateField() {
    this.ratesFormArray.push(this._editTaxBuilder.buildTaxRateControlInfo(0));
  }

  isAtTaxRateLimit(): boolean {
    return this.ratesFormArray.length === TAX_RATE_LIMIT;
  }

  deleteRate(position: number) {
    this.ratesFormArray.removeAt(position);
  }

  updateTrackingProperties(taxInfo: Map<string, any>) {
    this.editTaxAdditionalProperties = this._editTaxBuilder.buildEditTaxAdditionalProperties(
      this.currentTax,
      taxInfo
    );
  }

  onSave() {
    if (this.form.valid) {
      this._loggerService.log('saving tax info');

      this.onUpdateTax.emit(
        this._editTaxBuilder.buildTax(this.currentTax, fromJS(this.form.value))
      );
    }
  }

  private _detectChanges() {
    this._changeDetectorRef.markForCheck();
    this._changeDetectorRef.detectChanges();
  }

  private _validateDuplicatedRates(formArray: FormArray) {
    const uniqueRates = {};

    formArray.controls.forEach((control: FormControl) => {
      const value = control.value;
      const currentErrors = control.errors || {};

      const newErrors = {};

      if (isUndefined(value) || uniqueRates[value]) {
        newErrors['duplicatedRate'] = true;
      } else {
        uniqueRates[value] = true;
      }

      if (currentErrors['duplicatedRate'] !== newErrors['duplicatedRate']) {
        const errors = !!newErrors['duplicatedRate']
          ? extend({}, currentErrors, newErrors)
          : omit(currentErrors, 'duplicatedRate');

        control.markAsDirty();
        control.setErrors(!isEmpty(errors) ? errors : null);
      }
    });
  }

  ngOnDestroy() {
    this.form = null;
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
